import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  getTrustDecision,
  labelTrustDecision,
  type TrustAction,
  type TrustDecision,
  type TrustLabelKind,
  type TrustLayer,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { usePermission } from '../../hooks/usePermission'

const LABEL_KINDS: {
  kind: TrustLabelKind
  label: string
  help: string
  variant: 'primary' | 'success' | 'danger' | 'secondary'
}[] = [
  { kind: 'confirm', label: 'Confirm', help: 'Model decision was correct', variant: 'success' },
  { kind: 'false_positive', label: 'False positive', help: 'Flagged but actually safe', variant: 'danger' },
  { kind: 'false_negative', label: 'False negative', help: 'Allowed but should have been flagged', variant: 'danger' },
  { kind: 'ambiguous', label: 'Ambiguous', help: 'Edge case — needs policy discussion', variant: 'secondary' },
]

const ACTION_STYLES: Record<TrustAction, string> = {
  allow: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  review: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  block: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
}

const LAYER_STYLES: Record<TrustLayer, string> = {
  cache: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  rules: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  embeddings: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
  llm: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
  rekognition: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200',
  disabled: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
}

function MetaCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <dt className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {label}
      </dt>
      <dd className="text-sm text-gray-900 dark:text-gray-100">{children}</dd>
    </div>
  )
}

export function TrustDecisionDetail() {
  const { decisionId } = useParams<{ decisionId: string }>()
  const navigate = useNavigate()
  const canLabel = usePermission('trust', 'label')

  const [decision, setDecision] = useState<TrustDecision | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<TrustLabelKind | null>(null)
  const [notes, setNotes] = useState('')

  useEffect(() => {
    if (!decisionId) return
    let cancelled = false
    ;(async () => {
      try {
        const data = await getTrustDecision(decisionId)
        if (!cancelled) setDecision(data)
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [decisionId])

  async function applyLabel(kind: TrustLabelKind) {
    if (!decisionId) return
    setBusy(kind)
    setError(null)
    try {
      await labelTrustDecision(decisionId, { label: kind, notes: notes || undefined })
      navigate('/trust')
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Label failed')
      setBusy(null)
    }
  }

  if (error && !decision) {
    return (
      <div className="p-6 max-w-3xl">
        <Link to="/trust" className="text-sm text-[#7f4f9a] dark:text-[#b58fcc] hover:underline">
          ← Back to queue
        </Link>
        <div className="mt-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 p-4 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      </div>
    )
  }

  if (!decision) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  const labelEntries = Object.entries(decision.labels || {})

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      <div>
        <Link to="/trust" className="inline-flex items-center gap-1 text-sm text-[#7f4f9a] dark:text-[#b58fcc] hover:underline">
          ← Back to queue
        </Link>
      </div>

      <div className="rounded-2xl border border-[#ecdefc] dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#7f4f9a] dark:text-[#b58fcc]">
              Sentinel Decision
            </div>
            <h1 className="font-mono text-lg text-gray-900 dark:text-gray-100 break-all">
              {decision.decision_id}
            </h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className={`inline-flex px-2.5 py-1 rounded-md text-xs font-semibold uppercase tracking-wide ${ACTION_STYLES[decision.action]}`}>
              {decision.action}
            </span>
            <span className={`inline-flex px-2.5 py-1 rounded-md text-xs font-medium ${LAYER_STYLES[decision.layer]}`}>
              {decision.layer}
            </span>
            <span className="inline-flex px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
              {decision.surface}
            </span>
          </div>
        </div>

        <dl className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetaCell label="Score">
            <span className="font-mono">{decision.score.toFixed(3)}</span>
          </MetaCell>
          <MetaCell label="Latency">
            {decision.latency_ms} <span className="text-gray-500 dark:text-gray-400">ms</span>
          </MetaCell>
          <MetaCell label="Rule version">
            <span className="font-mono text-xs">{decision.rule_version}</span>
          </MetaCell>
          <MetaCell label="Model version">
            <span className="font-mono text-xs">{decision.model_version}</span>
          </MetaCell>
          <MetaCell label="Created">
            {new Date(decision.created_at).toLocaleString()}
          </MetaCell>
        </dl>
      </div>

      <div className="rounded-2xl border border-[#ecdefc] dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] p-6">
        <h2 className="font-heading text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Reasons
        </h2>
        {decision.reasons.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No reasons emitted.</p>
        ) : (
          <ul className="space-y-3">
            {decision.reasons.map((r, i) => (
              <li
                key={i}
                className="flex flex-col md:flex-row md:items-center md:gap-4 p-3 rounded-lg bg-[#fbf7ff] dark:bg-[#241b2e] border border-[#f0e3ff] dark:border-[#3d2c4f]"
              >
                <code className="text-xs font-mono text-[#7f4f9a] dark:text-[#b58fcc] shrink-0">
                  {r.code}
                </code>
                <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                  {r.description}
                </span>
                {r.score !== undefined && r.score !== null && (
                  <span className="text-xs font-mono text-gray-500 dark:text-gray-400 shrink-0">
                    score {r.score.toFixed(3)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-2xl border border-[#ecdefc] dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] p-6">
        <h2 className="font-heading text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Current labels
        </h2>
        {labelEntries.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No labels applied yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {labelEntries.map(([key, value]) => (
              <div
                key={key}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#f5edff] dark:bg-[#2a1f3d] border border-[#ecdefc] dark:border-[#3d2c4f]"
              >
                <span className="text-xs font-medium text-[#503864] dark:text-[#d0b8df]">{key}</span>
                <span className="font-mono text-xs text-gray-700 dark:text-gray-300">{value}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {canLabel && (
        <div className="rounded-2xl border border-[#ecdefc] dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] p-6">
          <h2 className="font-heading text-lg font-semibold text-gray-900 dark:text-gray-100">
            Apply moderator label
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">
            Your label feeds the training signal — be deliberate.
          </p>

          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Notes (optional)
            </label>
            <textarea
              className="mt-1 w-full rounded-md border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#0f0d12] px-3 py-2 text-sm"
              placeholder="Why are you assigning this label? Add context for future reviewers…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          {error && (
            <div className="mt-3 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 p-3 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {LABEL_KINDS.map((opt) => (
              <div
                key={opt.kind}
                className="flex flex-col gap-2 p-3 rounded-lg border border-[#f0e3ff] dark:border-[#3d2c4f] bg-[#fbf7ff] dark:bg-[#241b2e]"
              >
                <p className="text-xs text-gray-600 dark:text-gray-400">{opt.help}</p>
                <CustomButton
                  variant={opt.variant}
                  size="sm"
                  disabled={busy !== null}
                  loading={busy === opt.kind}
                  onClick={() => applyLabel(opt.kind)}
                >
                  {opt.label}
                </CustomButton>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
